/* WelcomeSpotlightTour — iter862 · full welcome walkthrough
 * ─────────────────────────────────────────────────────────────────
 * Jay: "everyone who does create a new account, it should have this
 * walkthrough... welcome to flyt... explore now... then it walks
 * through and highlights the main things" — Launch Pad, Daily Brief,
 * the Media Hub (emphasized most — "send upload link... this is your
 * storage"), FLYT link analytics, building the FLYT ID ("create a
 * stunning image"), then on Profile: game photos + a hand-off to the
 * existing Network walkthrough ("leave it at the end since when they
 * hit that it could go to the network walkthrough that already
 * exists").
 *
 * Two stages:
 *   1. A full-screen white "Welcome to Flyt" cover — black text, black
 *      pill button, "Explore Now" / "Skip".
 *   2. A two-page spotlight tour reusing NetworkProfileTour (the same
 *      SVG-mask spotlight engine the Network tour uses) — first a
 *      group of Home-page steps, then a group of Profile-page steps.
 *      Finishing the LAST Profile step navigates to the athlete's own
 *      Network profile — where the existing Network tour (iter473)
 *      naturally fires on its own gating (`!has_seen_network_tour`),
 *      so no separate hand-off logic is needed here.
 *
 * Persistent gate = `public_users.has_seen_app_tour`, the same field/
 * endpoint iter489's 3-step tour already used (this replaces it
 * entirely — nothing else reads those 3 old steps anymore).
 *
 * Manually replayable any time via the hamburger menu's "Walkthrough"
 * item (Navbar.js, athlete persona only), which navigates to
 * `/?tour=1`. This component force-opens on that param regardless of
 * `has_seen_app_tour`, then strips it so a refresh doesn't re-trigger
 * it. Each fresh run gets its own `runId` so the two NetworkProfileTour
 * instances remount (step index resets to 0) instead of resuming
 * wherever a previous run left off.
 *
 * Athlete-only — every step targets athlete-only surfaces (Launch Pad,
 * FLYT ID, Media Hub), so this no-ops entirely for any account with no
 * linked athlete slug.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import NetworkProfileTour from '../athlete/NetworkProfileTour';
import { useUserAuth } from '../../contexts/UserAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HOME_PATH = '/';
const PROFILE_PATH = '/profile';

const WelcomeSpotlightTour = () => {
  const { user, refreshUser } = useUserAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stage, setStage] = useState('idle'); // idle | cover | home | profile
  const [runId, setRunId] = useState(0);
  const [refImage, setRefImage] = useState(null);
  const closedRef = useRef(false);
  const slug = user?.athlete_slug || user?.linked_athlete || user?.linked_athlete_slug;

  // Auto-fire once for a brand-new athlete landing on Home.
  useEffect(() => {
    if (!user || !slug) return;
    if (user.has_seen_app_tour) return;
    if (closedRef.current) return;
    if (location.pathname !== HOME_PATH) return;
    const t = setTimeout(() => setStage('cover'), 1200);
    return () => clearTimeout(t);
  }, [user, slug, location.pathname]);

  // Manual replay — Navbar's "Walkthrough" item navigates to `/?tour=1`.
  // Force-opens regardless of has_seen_app_tour, then strips the param
  // so a refresh doesn't re-trigger it.
  useEffect(() => {
    if (!slug) return;
    if ((searchParams.get('tour') || '') !== '1') return;
    closedRef.current = false;
    setStage('cover');
    const next = new URLSearchParams(searchParams);
    next.delete('tour');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, slug]);

  // Pull the reference FLYT ID image live (Jay's own account) once
  // we're about to reach the step that shows it.
  useEffect(() => {
    if (stage !== 'home') return;
    if (refImage) return;
    axios.get(`${API}/onboarding/reference-flyt-id`).then((r) => {
      if (r.data?.available) setRefImage(r.data);
    }).catch(() => {});
  }, [stage, refImage]);

  const finish = useCallback(async () => {
    closedRef.current = true;
    setStage('idle');
    try {
      await axios.post(`${API}/me/app-tour/complete`);
      refreshUser?.().catch(() => {});
    } catch (_) { /* silent — retry next session at worst */ }
  }, [refreshUser]);

  const explore = useCallback(() => {
    setRunId((r) => r + 1);
    setStage('home');
    if (location.pathname !== HOME_PATH) navigate(HOME_PATH);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, navigate]);

  const homeSteps = useMemo(() => ([
    {
      id: 'launchpad',
      selector: '[data-testid="flyt-launchpad-island"]',
      title: 'Your Launch Pad',
      message: 'Your game-day hub — follow your school, see your schedule, and jump straight into whatever matters today.',
    },
    {
      id: 'daily-brief',
      selector: '[data-testid="daily-brief-open"]',
      title: 'Daily Brief',
      message: 'See scores happening around you, updated live.',
    },
    {
      id: 'media-hub-peek',
      selector: '[data-testid="athlete-home-recent-photos"], [data-testid="athlete-home-analytics"]',
      title: 'Your Media Hub',
      message: 'This is where every photo of you lives. Send an upload link to anyone — a photographer, fan, or parent — and it lands right in your storage, automatically.',
    },
    {
      id: 'analytics',
      selector: '[data-testid="athlete-home-analytics"]',
      title: 'FLYT link analytics',
      message: 'Track how many people are viewing your profile and tapping your links.',
    },
    {
      id: 'flyt-id',
      selector: '[data-testid="athlete-home-edit-flytid"], [data-testid="athlete-home-actions"]',
      title: 'Build your FLYT ID',
      message: 'Get set up now and create a stunning profile image, like this.',
      image: refImage?.image || null,
      imageAlt: refImage?.name ? `${refImage.name}'s FLYT ID` : 'Example FLYT ID',
    },
    {
      id: 'send-link',
      selector: '[data-testid="athlete-home-send-link"], [data-testid="athlete-home-actions"]',
      title: 'Send your first link',
      message: 'Tap here to send an upload link to your first photographer, fan, or parent — or create your first folder right in the Media Hub.',
    },
  ]), [refImage]);

  const profileSteps = useMemo(() => ([
    {
      id: 'game-photos',
      selector: '[data-testid="profile-game-media-beacon"], [data-testid="profile-avatar"], [data-testid="profile-avatar-empty"]',
      title: 'Your game photos',
      message: 'Every game photo a photographer or parent uploads for you will also show up right here.',
    },
    {
      id: 'network',
      // iter863 · Jay: this step kept re-highlighting the profile
      // picture — wrong target. "You're on the Network" is a real,
      // visible pill further down the card (`push-to-network-row` /
      // PushToNetworkPill, STATUS_UI.none/live both read "You're on
      // the Network"), so spotlight that instead.
      selector: '[data-testid="push-to-network-row"]',
      title: "You're already on the Network",
      message: 'Tap Finish to see how your public Network profile works.',
    },
  ]), []);

  const finishHome = useCallback(() => {
    setStage('profile');
    navigate(PROFILE_PATH);
  }, [navigate]);

  const finishProfile = useCallback(() => {
    finish();
    if (slug) navigate(`/${slug}`);
  }, [finish, navigate, slug]);

  if (!slug) return null;

  return (
    <>
      {stage === 'cover' && (
        <div
          className="fixed inset-0 z-[9999] bg-white flex flex-col items-center justify-center px-8 text-center"
          data-testid="welcome-tour-cover"
          role="dialog"
          aria-label="Welcome to Flyt"
        >
          <p className="font-display text-4xl sm:text-5xl uppercase text-black leading-tight mb-4">
            Welcome to Flyt{user?.name ? `, ${String(user.name).split(' ')[0]}` : ''}
          </p>
          <p className="text-sm sm:text-base text-black/60 max-w-xs mb-10">
            Quick look around — your Launch Pad, Media Hub, and FLYT ID. Skip anytime.
          </p>
          <button
            type="button"
            onClick={explore}
            className="px-8 py-3.5 rounded-full bg-black text-white text-sm font-black uppercase tracking-widest active:scale-[0.98] transition"
            data-testid="welcome-tour-explore"
          >
            Explore Now
          </button>
          <button
            type="button"
            onClick={finish}
            className="mt-5 text-xs uppercase tracking-widest text-black/40 font-bold"
            data-testid="welcome-tour-skip-cover"
          >
            Skip
          </button>
        </div>
      )}
      <NetworkProfileTour
        key={`home-${runId}`}
        open={stage === 'home'}
        steps={homeSteps}
        onComplete={finishHome}
        onSkipAll={finish}
      />
      <NetworkProfileTour
        key={`profile-${runId}`}
        open={stage === 'profile'}
        steps={profileSteps}
        onComplete={finishProfile}
        onSkipAll={finish}
      />
    </>
  );
};

export default WelcomeSpotlightTour;
